<script setup lang="ts">
import { computed } from "vue";
import { useBrandConfig } from "@composable/useBrandConfig";
import { useCoreServices } from "@composable/useCoreServices";
import { useLocale } from "@composable/useLocale";
import IncidentLabel from "@component/IncidentLabel.vue";
import IncidentDescription from "@component/IncidentDescription.vue";
import ReportList from "@component/ReportList.vue";

defineOptions({ inheritAttrs: false });
const props = withDefaults(defineProps<{ brand: string; uppercaseRegions?: boolean }>(), {
  uppercaseRegions: true,
});

const brandConfig = useBrandConfig(() => props.brand);
const locales = computed(() => brandConfig.value.locales);
const { locale } = useLocale();
const { report, incidents, regionNames, eventsBlockReport, logged, getTranslatedText } =
  useCoreServices(brandConfig);

// Index keys preserve the original positional incident selection during report updates.
// useCoreServices owns initial loading and polling; this component only renders the report.
</script>

<template>
  <div class="main-section-vue">
    <section class="rc-layout main-section">

      <div class="dashboard-title-wrapper">

        <div class="dashboard-title rc-layout-inner">
          <h1 class="dashboard-title-header">
            {{ getTranslatedText('Service Status', 'category.serviceStatus') }}
          </h1>

          <div class="core-services-info flex row">

            <input id="tab1" class="serv-tab1" type="radio" name="service-tab" checked>
            <label class="serv-label serv-label1" for="tab1">
              {{ getTranslatedText('Core Services', 'category.coreServices') }}
            </label>

            <div class="labels flex space-between">
              <div class="flex align-center">
                <div class="have-info-wrapper down board-ok">
                  <div class="have-info-icon">
                    <div class="have-info-content">
                      <div class="info-icon__tooltip">
                        <p>
                          {{ getTranslatedText('No issues are being reported', 'legend.ok') }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex align-center">
                <div class="have-info-wrapper down board-information">
                  <div class="have-info-icon">
                    <div class="have-info-content">
                      <div class="info-icon__tooltip">
                        <p>
                          {{ getTranslatedText('Information', 'legend.info') }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex align-center">
                <div class="have-info-wrapper down board-warn">
                  <div class="have-info-icon">
                    <div class="have-info-content">
                      <div class="info-icon__tooltip">
                        <p>
                          {{ getTranslatedText('We are currently investigating a potential issue', 'legend.warning') }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex align-center">
                <div class="have-info-wrapper down board-error">
                  <div class="have-info-icon">
                    <div class="have-info-content">
                      <div class="info-icon__tooltip">
                        <p>
                          {{ getTranslatedText('Issues are being reported', 'legend.error') }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="dashboard-wrapper flex flex-wrap">
        <div id="services-tab" class="services-tab">
          <div id="main-wrapper" class="rc-layout-inner">

            <!--    Locale changer-->
            <div v-if="Object.keys(locales).length > 1" class="locale-changer">
              <select v-model="locale">
                <option v-for="(name, localeCode) in locales" :key="`Lang${localeCode}`" :value="localeCode">{{ name }}</option>
              </select>
            </div>

            <!--Warnings block-->
            <div id="warnings-block" class="warning-block flex flex-wrap">
              <template v-for="(incident, index) in incidents" :key="index">
                <IncidentLabel :index="index" :incident="incident" />
                <IncidentDescription :incident="incident" />
              </template>
            </div>

            <!--Country block-->
            <div v-if="regionNames.length && !logged" class="country-block flex space-around">
              <span v-for="region in regionNames" :key="region">{{ props.uppercaseRegions ? region.toUpperCase() : region }}</span>
            </div>

            <!--Events block-->
            <div class="events-block">
              <template v-for="(reportData, service, index) in eventsBlockReport" :key="index">
                <ReportList
                  :region-names="regionNames"
                  :report="report"
                  :report-data="reportData"
                  :service="`${service}`"
                  :index-report="`${index}`"
                />
              </template>
            </div>


          </div>
        </div>
      </div>

    </section>

  </div>
</template>
